import {
  Box,
  Button,
  Checkbox,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react"
import {
  createFileRoute,
  getRouteApi,
  useNavigate,
} from "@tanstack/react-router"
import { useCallback, useEffect, useState } from "react"
import { FiPlus, FiSearch } from "react-icons/fi"
import Select from "@/components/Common/AppSelect"
import type { WarehousesDTO } from "../../../../client"
import { WmsListToolbar } from "../../../../components/WMS/shared/WmsListToolbar.tsx"
import { WmsPageHeader } from "../../../../components/WMS/shared/WmsPageHeader.tsx"
import {
  WmsPagePanel,
  WmsPageShell,
} from "../../../../components/WMS/shared/WmsPageShell.tsx"
import { AddWarehouse } from "../../../../components/WMS/Warehouses/AddWarehouse.tsx"
import { WarehousesList } from "../../../../components/WMS/Warehouses/WarehousesList.tsx"

type WarehousesListSearch = {
  q: string
  page: number
  page_size: number
  include_archived: boolean
}

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200] as const

export const Route = createFileRoute("/_layout/WMS/warehouses/warehouses-list")(
  {
    validateSearch: (search: Record<string, unknown>) => {
      const rawPage =
        typeof search.page === "number" ? search.page : Number(search.page ?? 1)
      const rawPageSize =
        typeof search.page_size === "number"
          ? search.page_size
          : Number(search.page_size ?? 50)
      const normalizedPageSize = PAGE_SIZE_OPTIONS.includes(
        rawPageSize as (typeof PAGE_SIZE_OPTIONS)[number],
      )
        ? rawPageSize
        : 50

      return {
        q: typeof search.q === "string" ? search.q : "",
        page: Number.isFinite(rawPage) && rawPage > 0 ? rawPage : 1,
        page_size: normalizedPageSize,
        include_archived:
          search.include_archived === true ||
          search.include_archived === "true" ||
          search.include_archived === "1",
      }
    },
    component: WarehousesListPage,
  },
)

const warehousesListRouteApi = getRouteApi(
  "/_layout/WMS/warehouses/warehouses-list",
)

function WarehousesListPage() {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const search = warehousesListRouteApi.useSearch() as WarehousesListSearch
  const navigate = useNavigate({ from: "/WMS/warehouses/warehouses-list" })
  const {
    q: query,
    page,
    page_size: pageSize,
    include_archived: includeArchived,
  } = search
  const [searchInput, setSearchInput] = useState(query)
  const [selected, setSelected] = useState<WarehousesDTO[]>([])
  const panelBg = useColorModeValue("white", "gray.800")
  const panelBorder = useColorModeValue("gray.200", "whiteAlpha.200")

  useEffect(() => {
    setSearchInput(query)
  }, [query])

  useEffect(() => {
    const normalizedInput = searchInput.trim()
    const normalizedQuery = query.trim()
    if (normalizedInput === normalizedQuery) return

    const timeoutId = window.setTimeout(() => {
      navigate({
        to: "/WMS/warehouses/warehouses-list",
        search: {
          q: searchInput,
          page: 1,
          page_size: pageSize,
          include_archived: includeArchived,
        },
      })
    }, 300)

    return () => window.clearTimeout(timeoutId)
  }, [includeArchived, navigate, pageSize, query, searchInput])

  const handleSelectionChange = useCallback((items: WarehousesDTO[]) => {
    setSelected(items)
  }, [])

  const updateSearch = useCallback(
    (
      patch: Partial<{
        page: number
        page_size: number
        include_archived: boolean
      }>,
    ) => {
      navigate({
        to: "/WMS/warehouses/warehouses-list",
        search: {
          q: query,
          page: patch.page ?? page,
          page_size: patch.page_size ?? pageSize,
          include_archived: patch.include_archived ?? includeArchived,
        },
      })
    },
    [includeArchived, navigate, page, pageSize, query],
  )

  return (
    <WmsPageShell>
      <WmsPageHeader
        title="Magazyny"
        description="Zarządzaj strukturą magazynową i przechodź do szczegółów jednym kliknięciem."
        gradient="linear(to-r, green.600, teal.600)"
        badge={`Zaznaczone: ${selected.length}`}
      />

      <WmsPagePanel bg={panelBg} borderColor={panelBorder}>
        <WmsListToolbar
          actionsWrap="nowrap"
          filters={
            <>
              <InputGroup maxW={{ base: "100%", lg: "520px" }}>
                <InputLeftElement pointerEvents="none">
                  <FiSearch />
                </InputLeftElement>
                <Input
                  size="sm"
                  placeholder="Szukaj magazyn (kod lub nazwa)"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  borderRadius="xl"
                />
              </InputGroup>
              <Select
                size="sm"
                maxW={{ base: "100%", lg: "170px" }}
                value={String(pageSize)}
                onChange={(event) =>
                  updateSearch({
                    page_size: Number(event.target.value),
                    page: 1,
                  })
                }
                borderRadius="xl"
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size} / strona
                  </option>
                ))}
              </Select>
              <Checkbox
                size="sm"
                isChecked={includeArchived}
                onChange={(event) =>
                  updateSearch({
                    include_archived: event.target.checked,
                    page: 1,
                  })
                }
                whiteSpace="nowrap"
              >
                Pokaż archiwalne
              </Checkbox>
            </>
          }
          actions={
            <Button
              size="sm"
              colorScheme="green"
              onClick={onOpen}
              leftIcon={<FiPlus />}
              borderRadius="xl"
            >
              Dodaj magazyn
            </Button>
          }
        />

        <Box flex="1" minH={0} overflow="hidden">
          <WarehousesList
            searchQuery={query}
            includeArchived={includeArchived}
            page={page}
            pageSize={pageSize}
            onChangePage={(nextPage) => updateSearch({ page: nextPage })}
            onSelectionChange={handleSelectionChange}
          />
        </Box>
      </WmsPagePanel>

      <Modal isOpen={isOpen} onClose={onClose} size="2xl">
        <ModalOverlay />
        <ModalContent borderRadius="2xl">
          <ModalHeader>Stworzyć magazyn</ModalHeader>
          <ModalBody pb={6}>
            <AddWarehouse onSuccess={onClose} onCancel={onClose} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </WmsPageShell>
  )
}
